import "./MobileApp.css";
import { useEffect, useRef, useState } from "react";
import useIsDesktop from "../../hooks/useIsDesktop";
import MobileOffers from "./mobileOffers/MobileOffers";
import MobileOfferDetail from "./mobileOffers/MobileOfferDetail";
import MobileContact from "./MobileContact";
import PresentationPage from "../common/PresentationPage";
import { offersApi } from "../../api";

const OFFERWALL_ROUTES = ["/offerwall", "/offerwallExternal"];
const EXTERNAL_OFFERWALL_ROUTE = "/offerwallExternal";

const mapAppToOffer = (app) => {
  if (!app) return null;
  return {
    ...app,
    id: app.packageName || app.appName || app.appId || app.id,
    title: app.displayName || app.appName || app.name || app.title,
    packageName: app.packageName || app.appId || app.id,
    icon: app.logo,
    reward: app.rewardTitle ?? app.reward,
    description: app.offerLongDesc || app.description,
    rewardConditions: app.rewardConditions,
    support: app.supportText || app.support,
    tagline: app.offerShortDesc || app.tagline,
  };
};

const normalizeAppsPayload = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.result?.apps)) return payload.result.apps;
  if (Array.isArray(payload.apps)) return payload.apps;
  return [];
};

const getExternalDeepLinkParams = () => {
  if (typeof window === "undefined") {
    return { userId: "", siteUserId: "", packageName: "", apiKey: "" };
  }
  const params = new URLSearchParams(window.location.search);
  return {
    userId: params.get("userId") || "",
    siteUserId: params.get("siteUserId") || "",
    packageName: params.get("packageName") || "",
    apiKey: params.get("apiKey") || "",
  };
};

export default function MobileApp({ allowDesktop = false }) {
  const isDesktop = useIsDesktop(1024);
  const isDesktopFrameMode = isDesktop && allowDesktop;
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [removedOfferKeys, setRemovedOfferKeys] = useState([]);
  const [mobileRoute, setMobileRoute] = useState(() => (typeof window !== "undefined" ? window.location.pathname : "/offerwall"));
  const [baseOfferwallRoute, setBaseOfferwallRoute] = useState(() => {
    if (typeof window === "undefined") return "/offerwall";
    return window.location.pathname === "/offerwallExternal" ? "/offerwallExternal" : "/offerwall";
  });
  const externalBootstrapStartedRef = useRef(false);

  const navigateMobile = (next) => {
    if (typeof window !== "undefined") {
      window.history.pushState({}, "", next);
    }
    setMobileRoute(next);
  };

  useEffect(() => {
    if (isDesktop && !allowDesktop && typeof window !== "undefined") {
      window.location.replace("/");
    }
  }, [isDesktop, allowDesktop]);


  useEffect(() => {
    if (typeof window === "undefined") return;
    const onPopState = () => setMobileRoute(window.location.pathname || "/offerwall");
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (OFFERWALL_ROUTES.includes(mobileRoute)) {
      setBaseOfferwallRoute(mobileRoute);
      return;
    }
    if (mobileRoute !== "/contact" && mobileRoute !== "/presentation") {
      navigateMobile(baseOfferwallRoute);
    }
  }, [mobileRoute, baseOfferwallRoute]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const isExternalRoute =
      baseOfferwallRoute === EXTERNAL_OFFERWALL_ROUTE && mobileRoute === EXTERNAL_OFFERWALL_ROUTE;
    if (!isExternalRoute) {
      externalBootstrapStartedRef.current = false;
      return;
    }
    if (externalBootstrapStartedRef.current) return;
    externalBootstrapStartedRef.current = true;

    const { userId, siteUserId, packageName, apiKey } = getExternalDeepLinkParams();

    if (!packageName || (!userId && !siteUserId)) return;

    let canceled = false;

    const openExternalDetailFromQuery = async () => {
      // Open detail immediately; MobileOfferDetail will refresh full offer data.
      setSelectedOffer({
        id: packageName,
        packageName,
        userId,
        siteUserId,
        title: packageName,
      });
      try {
        const payload = apiKey
          ? await offersApi.getOfferwallConfigByApiKey({
            apiKey,
            userId,
            siteUserId,
            packageName,
          })
          : await offersApi.getAllAppsForUser({
            userId,
            siteUserId,
            packageName,
          });
        if (canceled) return;

        const offers = normalizeAppsPayload(payload).map(mapAppToOffer).filter(Boolean);
        const targetPackage = packageName.trim().toLowerCase();
        const matchedOffer = offers.find(
          (item) => {
            const keys = [
              item?.packageName,
              item?.appName,
              item?.title,
              item?.displayName,
              item?.name,
              item?.appId,
              item?.id,
            ]
              .filter(Boolean)
              .map((value) => String(value).trim().toLowerCase());
            return keys.includes(targetPackage);
          }
        );
        const nextOffer = matchedOffer;
        if (!nextOffer) return;

        const enrichedOffer = {
          ...nextOffer,
          userId: nextOffer.userId || userId,
          siteUserId: nextOffer.siteUserId || siteUserId,
          packageName: nextOffer.packageName || packageName,
        };
        setSelectedOffer(enrichedOffer);
      } catch (err) {
        console.warn("external deep link offer bootstrap failed", err);
      }
    };

    openExternalDetailFromQuery();
    return () => {
      canceled = true;
    };
  }, [baseOfferwallRoute, mobileRoute]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const { packageName } = getExternalDeepLinkParams();
      const isExternalDeepLink =
        window.location.pathname === EXTERNAL_OFFERWALL_ROUTE && Boolean(packageName);
      if (isExternalDeepLink) return;
      const stored = window.sessionStorage.getItem("mobileSelectedOffer");
      if (stored) {
        setSelectedOffer(JSON.parse(stored));
      }
    } catch (err) {
      console.warn("mobileSelectedOffer restore failed", err);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (selectedOffer) {
        window.sessionStorage.setItem("mobileSelectedOffer", JSON.stringify(selectedOffer));
      } else {
        window.sessionStorage.removeItem("mobileSelectedOffer");
      }
    } catch (err) {
      console.warn("mobileSelectedOffer persist failed", err);
    }
  }, [selectedOffer]);

  if (isDesktop && !allowDesktop) return null;

  const content = mobileRoute === "/presentation" ? (
    <PresentationPage
      onBack={() => navigateMobile(baseOfferwallRoute)}
    />
  ) : mobileRoute === "/contact" ? (
    <MobileContact
      onBack={() => navigateMobile(baseOfferwallRoute)}
    />
  ) : selectedOffer ? (
    <MobileOfferDetail
      offer={selectedOffer}
      onBack={() => setSelectedOffer(null)}
      showBackButton={baseOfferwallRoute !== EXTERNAL_OFFERWALL_ROUTE}
      onRemoveActiveOffer={(offer) => {
        const key = offer?.id || offer?.packageName || offer?.appName;
        if (key) {
          setRemovedOfferKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
        }
        setSelectedOffer(null);
      }}
    />
  ) : (
    <MobileOffers onSelectOffer={setSelectedOffer} removedOfferKeys={removedOfferKeys} />
  );

  return (
    <div className={`mobile-app-shell${isDesktopFrameMode ? " mobile-app-shell--desktop" : ""}`}>
      <div className={isDesktopFrameMode ? "mobile-app-shell__frame" : ""}>
        {content}
      </div>
    </div>
  );
}
